import React from 'react';
import { history } from 'umi';
import { TableColumn } from 'tea-component';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import LinkJump from '@gp/components/LinkJump';
import RiskTag from '@gp/pages/StudioDashboard/StudioOverall/components/RiskTag';
import { getArraySort, queryJumpable } from '@gp/utils/common';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import { RISKS_CONFIGS } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management/Details/components/KeyIssues/utils/constants';

const getShowList = (data: IStudioOverallManagementKeyIssuesResultData) => {
  const res: IStudioOverallManagementKeyIssuesData = [];
  for (let i = 0;i < data?.length;i++) {
    const t = data?.[i];
    if (Array.isArray(t?.list) && t?.list?.length) {
      for (let j = 0;j < t?.list?.length;j++) {
        const k = t?.list?.[j];
        res.push({
          studio_id: t?.studio_id,
          studio_cover: t?.studio_cover,
          studio_name: t?.studio_name,
          business_owner: t?.business_owner,
          category: k?.category,
          risk_rating: k?.risk_rating,
          risk_challenge: k?.risk_challenge,
          next_steps: k?.next_steps,
          create_time: k?.create_time,
          uniqueId: `${t?.studio_id}-${j + 1}`,
          isMergeable: j !== 0,
        });
      }
    } else {
      for (let j = 0;j < 4;j++) {
        res.push({
          studio_id: t?.studio_id,
          studio_cover: t?.studio_cover,
          studio_name: t?.studio_name,
          business_owner: t?.business_owner,
          category: '',
          risk_rating: 0,
          risk_challenge: '',
          next_steps: '',
          create_time: '',
          uniqueId: `${t?.studio_id}-${j + 1}`,
          isMergeable: j !== 0,
        });
      }
    }
  }

  return res;
};

const toDetail = (studioId: string, studioName: string, isJump = true) => {
  if (!studioId) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      studioId,
      studioName,
      menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
      tab: 'management',
    }),
  );
  const url = `${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${filter}`;
  if (isJump) {
    history.push(url);
  }

  return url;
};

const getColumns = () => {
  const opt: TableColumn<any>[] = [
    {
      key: 'studio',
      header: () => (
        <hgroup className="table-header-category-type">
          <section className="table-header-category" title="Studio">
            Studio
          </section>
          {/* <section className="table-header-type">Type</section> */}
        </hgroup>
      ),
      width: '20%',
      align: 'left',
      // fixed: 'left',
      render: (cvm: any) => (
        <hgroup className="table-body-category-type">
          <section className={`table-body-category ${cvm?.isMergeable ? 'table-body-mergeable-category' : ''}`}>
            {cvm?.isMergeable ? null : (
              <section className="table-body-studio-col">
                <LinkJump url={toDetail(cvm?.studio_id, cvm?.studio_name, false)}>
                  <GeneralImageNew
                    className="dbg-cursor-pointer"
                    style={{ border: '1px solid rgba(0, 0, 0, 0.1)' }}
                    domain="studio"
                    width={40}
                    height={40}
                    src={cvm?.studio_cover}
                    onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                      if (!queryJumpable(e)) return;
                      toDetail(cvm?.studio_id, cvm?.studio_name);
                    }}
                  />
                </LinkJump>
                <LinkJump
                  style={{ textDecoration: 'none', display: 'flex', width: '100%' }}
                  url={toDetail(cvm?.studio_id, cvm?.studio_name, false)}
                >
                  <p
                    className="table-body-studio-col-studio-name dbg-cursor-pointer"
                    title={cvm?.studio_name || '-'}
                    onClick={(e) => {
                      if (!queryJumpable(e)) return;
                      toDetail(cvm?.studio_id, cvm?.studio_name);
                    }}
                  >
                    {cvm?.studio_name || '-'}
                  </p>
                </LinkJump>
                <p className="table-body-studio-col-bo" title={cvm?.business_owner || '-'}>
                  BO: {cvm?.business_owner || '-'}
                </p>
              </section>
            )}
          </section>
          {/* <section className="table-body-type">{cvm?.type || '-'}</section> */}
        </hgroup>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '20%',
      align: 'left',
      render: (cvm: any) => (
        <span className="dbg-word-break-word" title={cvm?.category || '-'}>
          {cvm?.category || '-'}
        </span>
      ),
    },
    {
      key: 'risk_rating',
      header: 'Risk Rating',
      width: '14%',
      align: 'left',
      render: (cvm: any) =>
        ['1', '2', '3', 1, 2, 3]?.includes(cvm?.risk_rating) ? (
          <RiskTag value={String(cvm?.risk_rating)} configs={RISKS_CONFIGS} />
        ) : null,
    },
    {
      key: 'risk_challenge',
      header: 'Risk / Challenge',
      width: '23%',
      align: 'left',
      render: (cvm: any) => (
        <span className="dbg-word-break-word" title={cvm?.risk_challenge || '-'}>
          {cvm?.risk_challenge || '-'}
        </span>
      ),
    },
    {
      key: 'next_steps',
      header: 'Next Steps',
      width: '23%',
      align: 'left',
      render: (cvm: any) => (
        <span className="dbg-word-break-word" title={cvm?.next_steps || '-'}>
          {cvm?.next_steps || '-'}
        </span>
      ),
    },
  ];

  return opt;
};

const getYearSelectOptions = (
  data: string[],
): {
  text: string;
  value: string;
}[] => {
  const options: {
    text: string;
    value: string;
  }[] = [];
  if (!Array.isArray(data) || !data?.length) return options;
  const sortData = getArraySort(data, 'number', 'desc');
  for (let i = 0;i < sortData?.length;i++) {
    const t = sortData?.[i];
    options.push({
      text: t,
      value: t,
    });
  }

  return options;
};

export { getColumns, getShowList, getYearSelectOptions };
